/**
 * Keyboard shortcut utility for Mac and Windows compatibility across the Outlet flows.
 */

export const isMac = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  return /(Mac|iPhone|iPod|iPad)/i.test(
    navigator.platform || navigator.userAgent || ""
  );
};

export const SHORTCUT_LABELS = {
  OUTLET_HEADER: () => (isMac() ? "⌥ + H" : "Alt + H"),
  NEXT_GROUP: () => (isMac() ? "⌥ + N or ⌥ + →" : "Alt + N or Alt + →"),
  PREV_GROUP: () => (isMac() ? "⌥ + P or ⌥ + ←" : "Alt + P or Alt + ←"),
  TOTAL_IMAGE: () => (isMac() ? "⌥ + I" : "Alt + I"),
  VIEW_IMAGES: () => (isMac() ? "⌥ + I" : "Alt + I"),
  SAVE_INVOICE_DETAILS: () => (isMac() ? "⌥ + D" : "Alt + D"),
  VALIDATE: () => (isMac() ? "⌥ + V" : "Alt + V"),
  VALIDATE_ACCOUNT: () => (isMac() ? "⌥ + V, A" : "Alt + V, A"),
  VALIDATE_IFSC: () => (isMac() ? "⌥ + V, A, I" : "Alt + V, A, I"),
  VALIDATE_GST: () => (isMac() ? "⌥ + V, G" : "Alt + V, G"),
  VALIDATE_PAN: () => (isMac() ? "⌥ + V, P" : "Alt + V, P"),
  VALIDATE_UPI: () => (isMac() ? "⌥ + V, U" : "Alt + V, U"),
  TOTAL_FILE: () => (isMac() ? "⌥ + F" : "Alt + F"),
  SAVE: () => (isMac() ? "⌥ + S or ⌘ + Shift + S" : "Alt + S or Ctrl + Shift + S"),
  RESET: () => (isMac() ? "⌥ + R" : "Alt + R"),
  UPDATE_STATUS: () => (isMac() ? "⌥ + U" : "Alt + U"),
  QC_EDIT_HISTORY: () => (isMac() ? "⌥ + Q" : "Alt + Q"),
  LOGS: () => (isMac() ? "⌥ + L" : "Alt + L"),
  SURVEY_DETAILS: () => (isMac() ? "⌥ + W" : "Alt + W"),
  ANSWER_LOG: () => (isMac() ? "⌥ + A" : "Alt + A"),
};

export const getShortcutLabel = (actionKey) => {
  const getter = SHORTCUT_LABELS[actionKey];
  return getter ? getter() : "";
};

export const isAnyModalOpen = () => {
  return false;
};

/**
 * Outlet Header: Alt + H (Option + H on Mac)
 */
export const isOutletHeaderShortcut = (e) => {
  if (!e.altKey || e.ctrlKey || e.metaKey) return false;
  return (
    e.code === "KeyH" ||
    e.keyCode === 72 ||
    e.which === 72 ||
    e.key?.toLowerCase() === "h" ||
    e.key === "˙"
  );
};

/**
 * Next Group: Alt + N or Alt + ArrowRight (Option + N or Option + ArrowRight on Mac)
 */
export const isNextGroupShortcut = (e) => {
  if (!e.altKey || e.ctrlKey || e.metaKey) return false;
  const isN =
    e.code === "KeyN" ||
    e.keyCode === 78 ||
    e.which === 78 ||
    e.key?.toLowerCase() === "n" ||
    e.key === "~";
  const isRight =
    e.code === "ArrowRight" ||
    e.keyCode === 39 ||
    e.which === 39 ||
    e.key === "ArrowRight";
  return isN || isRight;
};

/**
 * Previous Group: Alt + P or Alt + ArrowLeft (Option + P or Option + ArrowLeft on Mac)
 */
export const isPrevGroupShortcut = (e) => {
  if (!e.altKey || e.ctrlKey || e.metaKey) return false;
  const isP =
    e.code === "KeyP" ||
    e.keyCode === 80 ||
    e.which === 80 ||
    e.key?.toLowerCase() === "p" ||
    e.key === "π";
  const isLeft =
    e.code === "ArrowLeft" ||
    e.keyCode === 37 ||
    e.which === 37 ||
    e.key === "ArrowLeft";
  return isP || isLeft;
};

/**
 * Total Image / View Images: Alt + I (Option + I on Mac)
 */
export const isTotalImageShortcut = (e) => {
  if (!e.altKey || e.ctrlKey || e.metaKey) return false;
  return (
    e.code === "KeyI" ||
    e.keyCode === 73 ||
    e.which === 73 ||
    e.key?.toLowerCase() === "i" ||
    e.key === "ˆ" ||
    e.code === "KeyM" ||
    e.keyCode === 77 ||
    e.which === 77 ||
    e.key?.toLowerCase() === "m" ||
    e.key === "µ"
  );
};

export const isViewImagesShortcut = isTotalImageShortcut;

/**
 * Save Invoice Details: Alt + D (Option + D on Mac)
 */
export const isSaveInvoiceDetailsShortcut = (e) => {
  if (!e.altKey || e.ctrlKey || e.metaKey) return false;
  return (
    e.code === "KeyD" ||
    e.keyCode === 68 ||
    e.which === 68 ||
    e.key?.toLowerCase() === "d" ||
    e.key === "∂"
  );
};

/**
 * Validate: Alt + V (Option + V on Mac)
 */
export const isValidateShortcut = (e) => {
  if (e.altKey && !e.ctrlKey && !e.metaKey) {
    return (
      e.code === "KeyV" ||
      e.keyCode === 86 ||
      e.which === 86 ||
      e.key?.toLowerCase() === "v" ||
      e.key === "√"
    );
  }
  if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
    return (
      e.code === "KeyV" ||
      e.keyCode === 86 ||
      e.which === 86 ||
      e.key?.toLowerCase() === "v"
    );
  }
  return false;
};

/**
 * Total Files: Alt + F (Option + F on Mac)
 */
export const isTotalFileShortcut = (e) => {
  if (!e.altKey || e.ctrlKey || e.metaKey) return false;
  return (
    e.code === "KeyF" ||
    e.keyCode === 70 ||
    e.which === 70 ||
    e.key?.toLowerCase() === "f" ||
    e.key === "ƒ"
  );
};

/**
 * Save: Alt + S (Option + S on Mac) or Ctrl + Shift + S / Cmd + Shift + S or Ctrl + S / Cmd + S
 */
export const isSaveShortcut = (e) => {
  const isKeyS =
    e.code === "KeyS" ||
    e.keyCode === 83 ||
    e.which === 83 ||
    e.key?.toLowerCase() === "s" ||
    e.key === "ß" ||
    e.key === "§";

  if (!isKeyS) return false;

  // Alt + S (or Option + S on Mac)
  const isAltS = e.altKey && !e.ctrlKey && !e.metaKey;

  // Ctrl + Shift + S or Cmd + Shift + S
  const isCtrlOrCmdShiftS = (e.ctrlKey || e.metaKey) && e.shiftKey;

  // Ctrl + S or Cmd + S
  const isCtrlOrCmdS = (e.ctrlKey || e.metaKey) && !e.altKey;

  return isAltS || isCtrlOrCmdShiftS || isCtrlOrCmdS;
};

/**
 * Reset: Alt + R (Option + R on Mac)
 */
export const isResetShortcut = (e) => {
  if (!e.altKey || e.ctrlKey || e.metaKey) return false;
  return (
    e.code === "KeyR" ||
    e.keyCode === 82 ||
    e.which === 82 ||
    e.key?.toLowerCase() === "r" ||
    e.key === "®"
  );
};

/**
 * Update Status: Alt + U (Option + U on Mac)
 */
export const isUpdateStatusShortcut = (e) => {
  if (!e.altKey || e.ctrlKey || e.metaKey) return false;
  return (
    e.code === "KeyU" ||
    e.keyCode === 85 ||
    e.which === 85 ||
    e.key?.toLowerCase() === "u" ||
    e.key === "¨"
  );
};

/**
 * QC Edit History: Alt + Q (Option + Q on Mac)
 */
export const isQcEditHistoryShortcut = (e) => {
  if (!e.altKey || e.ctrlKey || e.metaKey) return false;
  return (
    e.code === "KeyQ" ||
    e.keyCode === 81 ||
    e.which === 81 ||
    e.key?.toLowerCase() === "q" ||
    e.key === "œ" ||
    e.key === "Œ"
  );
};

/**
 * Logs: Alt + L (Option + L on Mac)
 */
export const isLogsShortcut = (e) => {
  if (!e.altKey || e.ctrlKey || e.metaKey) return false;
  return (
    e.code === "KeyL" ||
    e.keyCode === 76 ||
    e.which === 76 ||
    e.key?.toLowerCase() === "l" ||
    e.key === "¬"
  );
};

/**
 * Survey Details: Alt + W (Option + W on Mac)
 */
export const isSurveyDetailsShortcut = (e) => {
  if (!e.altKey || e.ctrlKey || e.metaKey) return false;
  return (
    e.code === "KeyW" ||
    e.keyCode === 87 ||
    e.which === 87 ||
    e.key?.toLowerCase() === "w" ||
    e.key === "∑"
  );
};

/**
 * Answer Log / Answer: Alt + A (Option + A on Mac)
 */
export const isAnswerLogShortcut = (e) => {
  if (!e.altKey || e.ctrlKey || e.metaKey) return false;
  return (
    e.code === "KeyA" ||
    e.keyCode === 65 ||
    e.which === 65 ||
    e.key?.toLowerCase() === "a" ||
    e.key === "å" ||
    e.key === "Å"
  );
};

export const isAnswerShortcut = isAnswerLogShortcut;

/**
 * Moves focus to the next visible, editable input/select/textarea in the form container on Enter.
 */
export const focusNextInputField = (e) => {
  if (!e) return;
  const currentTarget = e.target;
  if (!currentTarget) return;

  const container =
    currentTarget.closest(".question-container") ||
    document.querySelector(".question-container") ||
    currentTarget.closest("form") ||
    document.body;

  if (!container) return;

  const focusableSelector =
    'input:not([disabled]):not([readonly]):not([tabindex="-1"]):not([type="hidden"]):not([type="file"]):not([type="button"]):not([type="submit"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([readonly]):not([tabindex="-1"])';

  const allElements = Array.from(
    container.querySelectorAll(focusableSelector)
  ).filter((el) => {
    const isParentDisabled = el.closest('[disabled], .disabled, [data-disabled="true"], fieldset:disabled');
    return (
      el.offsetParent !== null &&
      !el.disabled &&
      !el.readOnly &&
      !el.hasAttribute('disabled') &&
      !el.hasAttribute('readonly') &&
      el.tabIndex !== -1 &&
      !isParentDisabled &&
      window.getComputedStyle(el).display !== "none" &&
      window.getComputedStyle(el).visibility !== "hidden" &&
      window.getComputedStyle(el).pointerEvents !== "none"
    );
  });

  const currentIndex = allElements.indexOf(currentTarget);
  if (currentIndex > -1 && currentIndex < allElements.length - 1) {
    const nextEl = allElements[currentIndex + 1];
    nextEl.focus();
    if (
      nextEl.select &&
      typeof nextEl.select === "function" &&
      nextEl.type !== "date"
    ) {
      nextEl.select();
    }
  }
};

/**
 * Verification Chord Manager
 * Supports:
 * - Alt + V, A: Account verification
 * - Alt + V, A, I (or Alt + V, I): IFSC verification
 * - Alt + V, G: GST verification
 * - Alt + V, P: PAN verification
 * - Alt + V (alone, fallback): Default verification
 */
export const createVerificationChordManager = ({
  onVerifyAccount,
  onVerifyIfsc,
  onVerifyGst,
  onVerifyPan,
  onVerifyUpi,
  onVerifyDefault,
  timeoutMs = 1500,
  accountDebounceMs = 900,
}) => {
  let chordState = null; // null | 'WAITING_FOR_ACTION' | 'WAITING_FOR_IFSC_OR_ACCOUNT'
  let chordTimer = null;
  let accountTimer = null;

  const clearTimers = () => {
    if (chordTimer) {
      clearTimeout(chordTimer);
      chordTimer = null;
    }
    if (accountTimer) {
      clearTimeout(accountTimer);
      accountTimer = null;
    }
  };

  const resetChord = () => {
    clearTimers();
    chordState = null;
  };

  const isPrefixKey = (e) => {
    return isValidateShortcut(e);
  };

  const isActionKey = (e, char) => {
    const target = char.toLowerCase();
    const codeMap = {
      a: "KeyA",
      g: "KeyG",
      p: "KeyP",
      i: "KeyI",
      u: "KeyU",
    };
    const keyMap = {
      a: ["a", "å", "Å"],
      g: ["g", "©"],
      p: ["p", "π", "Π"],
      i: ["i", "ˆ", "Í"],
      u: ["u", "¨"],
    };
    const keyCodeMap = {
      a: 65,
      g: 71,
      p: 80,
      i: 73,
      u: 85,
    };

    if (e.code === codeMap[target]) return true;
    if (e.keyCode === keyCodeMap[target] || e.which === keyCodeMap[target]) return true;
    const pressedKey = e.key ? e.key.toLowerCase() : "";
    if (pressedKey === target) return true;
    if (keyMap[target] && keyMap[target].includes(e.key)) return true;
    return false;
  };

  // Reset chord on mouse click anywhere
  const handleGlobalMouseDown = () => {
    if (chordState) {
      resetChord();
    }
  };

  if (typeof window !== "undefined") {
    window.addEventListener("mousedown", handleGlobalMouseDown, true);
  }

  const handleKeyDown = (e) => {
    if (isAnyModalOpen()) {
      resetChord();
      return false;
    }

    // 1. Idle state: waiting for Alt + V prefix
    if (!chordState) {
      if (isPrefixKey(e)) {
        e.preventDefault();
        e.stopPropagation();
        chordState = "WAITING_FOR_ACTION";
        clearTimers();
        chordTimer = setTimeout(() => {
          if (chordState === "WAITING_FOR_ACTION") {
            resetChord();
            if (typeof onVerifyDefault === "function") {
              onVerifyDefault();
            }
          }
        }, timeoutMs);
        return true;
      }
      return false;
    }

    // 2. In WAITING_FOR_ACTION: looking for G, P, I, A, U
    if (chordState === "WAITING_FOR_ACTION") {
      // GST: Alt + V, G
      if (isActionKey(e, "g")) {
        e.preventDefault();
        e.stopPropagation();
        resetChord();
        if (typeof onVerifyGst === "function") {
          onVerifyGst();
        }
        return true;
      }

      // PAN: Alt + V, P
      if (isActionKey(e, "p")) {
        e.preventDefault();
        e.stopPropagation();
        resetChord();
        if (typeof onVerifyPan === "function") {
          onVerifyPan();
        }
        return true;
      }

      // UPI: Alt + V, U
      if (isActionKey(e, "u")) {
        e.preventDefault();
        e.stopPropagation();
        resetChord();
        if (typeof onVerifyUpi === "function") {
          onVerifyUpi();
        }
        return true;
      }

      // IFSC (direct): Alt + V, I
      if (isActionKey(e, "i")) {
        e.preventDefault();
        e.stopPropagation();
        resetChord();
        if (typeof onVerifyIfsc === "function") {
          onVerifyIfsc(false);
        }
        return true;
      }

      // Account: Alt + V, A (can be followed by I for IFSC)
      if (isActionKey(e, "a")) {
        e.preventDefault();
        e.stopPropagation();
        clearTimers();

        // If IFSC verification is not supported in this view, trigger Account immediately
        if (typeof onVerifyIfsc !== "function") {
          resetChord();
          if (typeof onVerifyAccount === "function") {
            onVerifyAccount();
          }
          return true;
        }

        chordState = "WAITING_FOR_IFSC_OR_ACCOUNT";

        accountTimer = setTimeout(() => {
          resetChord();
          if (typeof onVerifyAccount === "function") {
            onVerifyAccount();
          }
        }, accountDebounceMs);

        chordTimer = setTimeout(() => {
          resetChord();
        }, timeoutMs);
        return true;
      }

      // Alt + V pressed again: reset timer
      if (isPrefixKey(e)) {
        e.preventDefault();
        e.stopPropagation();
        clearTimers();
        chordTimer = setTimeout(() => {
          resetChord();
          if (typeof onVerifyDefault === "function") {
            onVerifyDefault();
          }
        }, timeoutMs);
        return true;
      }

      // Modifier keys: keep chord alive
      if (["Alt", "Shift", "Control", "Meta"].includes(e.key)) {
        return false;
      }

      // Escape: cancel chord
      if (e.key === "Escape" || e.code === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        resetChord();
        return true;
      }

      // Any other key cancels chord
      resetChord();
      return false;
    }

    // 3. In WAITING_FOR_IFSC_OR_ACCOUNT: looking for I (or timeout/Enter/Space -> Account)
    if (chordState === "WAITING_FOR_IFSC_OR_ACCOUNT") {
      // IFSC: Alt + V, A, I
      if (isActionKey(e, "i")) {
        e.preventDefault();
        e.stopPropagation();
        resetChord();
        if (typeof onVerifyIfsc === "function") {
          onVerifyIfsc(true);
        }
        return true;
      }

      // Immediate confirmation of Account via Enter or Space
      if (e.key === "Enter" || e.key === " " || e.code === "Space" || e.code === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        resetChord();
        if (typeof onVerifyAccount === "function") {
          onVerifyAccount();
        }
        return true;
      }

      // Modifier keys: keep waiting
      if (["Alt", "Shift", "Control", "Meta"].includes(e.key)) {
        return false;
      }

      // Escape: cancel chord without triggering
      if (e.key === "Escape" || e.code === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        resetChord();
        return true;
      }

      // Any other key triggers Account and resets chord
      resetChord();
      if (typeof onVerifyAccount === "function") {
        onVerifyAccount();
      }
      return false;
    }

    return false;
  };

  const destroy = () => {
    resetChord();
    if (typeof window !== "undefined") {
      window.removeEventListener("mousedown", handleGlobalMouseDown, true);
    }
  };

  return {
    handleKeyDown,
    resetChord,
    destroy,
  };
};


